import { Box, Grid, Paper, Typography, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import {
    Assignment,
    CheckCircle,
    ListAlt,
    Loyalty,
    Cancel,
    ShoppingCart,
    TwoWheeler,
} from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import { useModulosFornecedorContext } from "../context/useModulosFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitleText: {
        color: "#7f8c8d",
        marginTop: theme.spacing(0.5),
        [theme.breakpoints.down("sm")]: { fontSize: "0.9rem" },
    },
    moduleCard: {
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    moduleCardContratado: {
        borderTop: "4px solid #4caf50",
    },
    moduleCardNaoContratado: {
        borderTop: "4px solid var(--cor-erro)",
    },
    moduleCardHeader: {
        padding: theme.spacing(2.5, 2.5, 1.5),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    moduleIconContratado: {
        color: "#4caf50",
        fontSize: 36,
    },
    moduleIconNaoContratado: {
        color: "var(--cor-erro)",
        fontSize: 36,
    },
    statusIcon: {
        fontSize: 20,
    },
    statusIconContratado: {
        color: "#4caf50",
    },
    statusIconNaoContratado: {
        color: "var(--cor-erro)",
    },
    moduleCardBody: {
        padding: theme.spacing(0, 2.5, 2.5),
        flexGrow: 1,
    },
    moduleName: {
        fontWeight: 700,
        color: "#2c3e50",
        fontSize: "1rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    moduleStatus: {
        fontWeight: 600,
        fontSize: "0.85rem",
        marginTop: theme.spacing(0.5),
    },
    moduleStatusContratado: {
        color: "#4caf50",
    },
    moduleStatusNaoContratado: {
        color: "var(--cor-erro)",
    },
    infoCard: {
        borderRadius: 12,
        padding: theme.spacing(2),
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
        marginTop: theme.spacing(3),
    },
    infoText: {
        color: "#7f8c8d",
        fontSize: "0.9rem",
    },
}))

type ModuloCardProps = {
    nome: string
    contratado: boolean
    icone: React.ReactNode
}

function ModuloCard({ nome, contratado, icone }: ModuloCardProps) {
    const classes = useStyles()
    return (
        <Paper
            className={`${classes.moduleCard} ${
                contratado ? classes.moduleCardContratado : classes.moduleCardNaoContratado
            }`}
        >
            <Box className={classes.moduleCardHeader}>
                <Box className={contratado ? classes.moduleIconContratado : classes.moduleIconNaoContratado}>
                    {icone}
                </Box>
                {contratado ? (
                    <CheckCircle
                        className={`${classes.statusIcon} ${classes.statusIconContratado}`}
                    />
                ) : (
                    <Cancel
                        className={`${classes.statusIcon} ${classes.statusIconNaoContratado}`}
                    />
                )}
            </Box>
            <Box className={classes.moduleCardBody}>
                <Typography className={classes.moduleName}>{nome}</Typography>
                <Typography
                    className={`${classes.moduleStatus} ${
                        contratado ? classes.moduleStatusContratado : classes.moduleStatusNaoContratado
                    }`}
                >
                    {contratado ? "Contratado" : "Não contratado"}
                </Typography>
            </Box>
        </Paper>
    )
}

export function ModulosContratados() {
    const classes = useStyles()
    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, erroCarregamento } = useModulosFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const modulos: ModuloCardProps[] = [
        { nome: "Fidelidade", contratado: dados.fidelidade, icone: <Loyalty fontSize="inherit" /> },
        { nome: "Delivery", contratado: dados.delivery, icone: <ShoppingCart fontSize="inherit" /> },
        { nome: "Entregadores", contratado: dados.entregadores, icone: <TwoWheeler fontSize="inherit" /> },
        { nome: "Pedidos da Mesa", contratado: dados.pedidosMesa, icone: <Assignment fontSize="inherit" /> },
        { nome: "Central de Pedidos", contratado: dados.centralPedidos, icone: <ListAlt fontSize="inherit" /> },
    ]

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={isSmall ? 3 : 4}>
                    <Typography variant={isSmall ? "h5" : "h4"} className={classes.titleText}>
                        Módulos Contratados
                    </Typography>
                    <Typography variant="body2" className={classes.subtitleText}>
                        Aqui você tem acesso aos módulos que podem melhorar sua experiência no aplicativo.
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {modulos.map(modulo => (
                        <Grid item xs={12} sm={6} md={4} key={modulo.nome}>
                            <ModuloCard {...modulo} />
                        </Grid>
                    ))}
                </Grid>

                <Paper className={classes.infoCard} elevation={0}>
                    <Typography className={classes.infoText}>
                        Para habilitar ou desabilitar módulos, acione o nosso Suporte.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    )
}
